import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Monogram from '../components/layout/Monogram';
import NewsletterSignup from '../components/NewsletterSignup';
import { useUser } from '../contexts/UserContext';

interface HomeMentor {
  id: string;
  displayName: string;
  bio: string;
  university: string | null;
  major: string | null;
  classYear: string | null;
  mentorType?: string;
  occupation?: string | null;
  almaMater?: string | null;
  pricePerSession: number;
  achievements: Array<{
    id: string;
    category: string;
    title: string;
    isPrimary: boolean;
    role?: string | null;
  }>;
  specialties: Array<{ id: string; title: string; category: string }>;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const SUGGESTIONS = [
  'FTC',
  'First research paper',
  'USACO training plan',
  'Business plan competitions',
];

const TICKER = [
  'How do we email a coach without sounding desperate?',
  'What does a real research mentor expect from a 16-year-old?',
  'Which activities actually mattered on my application?',
  'How much should we spend on a private coach?',
  'Deferred early — what happens now?',
  'Can she compete internationally and still take five APs?',
];

const RESOURCES: { kind: string; title: string; blurb: string; action: string; to: string }[] = [
  {
    kind: 'Deadline calendar',
    title: 'Every major competition deadline, by month',
    blurb: 'Registration windows for the math, science, computing, and robotics pipelines — filter by grade.',
    action: 'Open the calendar',
    to: '/resources/calendar',
  },
  {
    kind: 'Grade-by-grade map',
    title: 'What matters in 7th grade vs 11th',
    blurb: 'Stage-by-stage: what to focus on now, what can wait, and the common mistake at each grade.',
    action: 'Read the map',
    to: '/resources/activity-map',
  },
  {
    kind: 'Weekly tips',
    title: 'One concrete thing to do this week',
    blurb: 'A specific, doable action every week, tagged by grade band.',
    action: 'Read this week’s tip',
    to: '/resources/tips',
  },
  {
    kind: 'Q&A digest',
    title: 'Real questions, answered by people who lived them',
    blurb: 'The questions families actually ask, answered by verified mentors.',
    action: 'Read the answers',
    to: '/resources/qa',
  },
  {
    kind: 'Webinars',
    title: 'Live sessions and full recordings',
    blurb: 'Free sessions with mentors and veteran parents — recordings included.',
    action: 'Browse the library',
    to: '/resources/webinars',
  },
  {
    kind: 'Data',
    title: 'What admitted students really did',
    blurb: 'Five years of activity profiles from admits, with the outliers marked.',
    action: 'Explore the data',
    to: '/data',
  },
  {
    kind: 'Practice',
    title: 'AMC 10 & 12 practice tests',
    blurb: 'Real past contests with timing, scoring, and analytics on every miss.',
    action: 'Start practicing',
    to: '/amc',
  },
  {
    kind: 'Admit profiles',
    title: 'Full anonymized profiles of real admits',
    blurb: 'Scores, activities, awards, essays, and every admission decision — filter by university and year.',
    action: 'Browse the profiles',
    to: '/resources/admit-profiles',
  },
];

const WEBINARS = [
  {
    month: 'Sep',
    day: '04',
    title: 'Recruiting timelines for grades 8–10',
    meta: '45 min · veteran competition parents welcome',
  },
  {
    month: 'Sep',
    day: '18',
    title: 'Ask a former admissions reader anything',
    meta: '60 min · parents welcome',
  },
  {
    month: 'Oct',
    day: '02',
    title: 'Getting a middle schooler into real research',
    meta: '45 min · with a published researcher',
  },
];

function getCardBadge(m: HomeMentor): string {
  if (m.mentorType === 'parent') return 'Parent Mentor';
  const primary = m.achievements?.find((a) => a.isPrimary) || m.achievements?.[0];
  return primary?.title || primary?.category || 'Mentor';
}

function getCredential(m: HomeMentor): string {
  if (m.mentorType === 'parent') {
    return (
      [m.occupation, m.almaMater ? `${m.almaMater} alum` : null].filter(Boolean).join(' · ') ||
      'Veteran competition parent'
    );
  }
  const primary = m.achievements?.find((a) => a.isPrimary) || m.achievements?.[0];
  const school = [m.university, m.classYear ? `'${String(m.classYear).slice(-2)}` : null]
    .filter(Boolean)
    .join(' ');
  return [primary?.title, school || m.major].filter(Boolean).join(' · ') || m.bio || '';
}

function getTags(m: HomeMentor): string[] {
  const fromSpecialties = (m.specialties || []).map((s) => s.title);
  if (fromSpecialties.length > 0) return fromSpecialties.slice(0, 2);
  return Array.from(new Set((m.achievements || []).map((a) => a.category))).slice(0, 2);
}

const HomePage: React.FC = () => {
  const { user } = useUser();
  const [mentors, setMentors] = useState<HomeMentor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/mentors`)
      .then((response) => {
        if (!cancelled) setMentors(response.data.data || response.data || []);
      })
      .catch((error) => console.error('Error fetching mentors:', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    return mentors.slice(0, 3); // Show only 3 mentors on homepage
  }, [mentors]);

  return (
    <div className="home">
      {/* Hero */}
      <section className="home-hero">
        <div className="home-hero-glow one" />
        <div className="home-hero-glow two" />
        <div className="home-hero-inner">
          <h1>Mentorship for <em className="accent">extraordinary</em> journeys.</h1>

          <p className="home-hero-sub">
            For families with students in grades 6–12 — connect with world champions, elite college
            admits, published researchers, founders, and experienced parents who know what it takes —
            from the decisions that matter early to the details that separate good from exceptional.
          </p>

          <div className="home-search-wrap">
            <Link className="home-search" to="/mentors" style={{ textDecoration: 'none' }}>
              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9A857D"
                strokeWidth="2"
                strokeLinecap="round"
                style={{ flex: 'none' }}
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M16.5 16.5 21 21" />
              </svg>
              <span style={{ flex: 1, color: 'var(--ink-faint)', padding: '12px 0' }}>
                Search an activity or ask a question
              </span>
              <span className="btn btn-primary" style={{ borderRadius: 10 }}>Find a mentor</span>
            </Link>
            <div className="home-suggest">
              <span className="try">Try</span>
              {SUGGESTIONS.map((label) => (
                <Link key={label} className="home-suggest-chip" to={`/mentors?search=${encodeURIComponent(label)}`}>
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Question ticker */}
      <div className="home-ticker">
        <div className="home-ticker-track">
          {TICKER.concat(TICKER).map((text, i) => (
            <span key={i} className="home-ticker-item">
              <span className="home-dot" />
              {text}
            </span>
          ))}
        </div>
      </div>

      {/* Mentor grid */}
      <section id="mentors" className="home-mentors">
        <div className="home-mentors-inner">
          <div className="home-mentors-head">
            <div>
              <div className="eyebrow-lg">The room</div>
              <span className="home-verified-badge">
                <span className="home-dot" />
                All mentor credentials are verified
              </span>
            </div>
            <Link to="/mentors" className="home-see-all">See all →</Link>
          </div>

          <div className="home-grid">
            {visible.map((m) => (
              <Link key={m.id} className="home-mentor-card" to={`/mentors/${m.id}`}>
                <div className="home-mentor-media">
                  <Monogram name={m.displayName} size={72} />
                  <div className="home-mentor-cat">{getCardBadge(m)}</div>
                </div>
                <div className="home-mentor-body">
                  <div className="home-mentor-toprow">
                    <div className="home-mentor-name">{m.displayName}</div>
                    <div className="home-mentor-price">${m.pricePerSession} / hr</div>
                  </div>
                  <div className="home-mentor-cred">{getCredential(m)}</div>
                  <div className="home-mentor-tags">
                    {getTags(m).map((tag) => (
                      <span key={tag} className="home-mentor-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {!loading && visible.length === 0 && (
            <div className="home-empty">
              <div className="big">No exact match yet.</div>
              <p>Send us the question and we will find the person who has answered it before.</p>
            </div>
          )}

        </div>
      </section>

      {/* Free resources */}
      <section id="resources" className="home-sec">
        <div className="home-sec-inner">
          <div className="home-sec-head">
            <div className="eyebrow-lg">Free resources</div>
            <Link to="/data" className="home-see-all">See all →</Link>
          </div>
          <div className="home-resources-grid">
            {RESOURCES.map((r) => (
              <Link
                key={r.title}
                className="home-resource-card"
                to={r.to === '/amc' && !user ? '/amc/sample' : r.to}
              >
                <div className="home-resource-kind">{r.kind}</div>
                <div className="home-resource-title">{r.title}</div>
                <div className="home-resource-blurb">{r.blurb}</div>
                <div className="home-resource-action">{r.action}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Live webinars */}
      <section id="webinars" className="home-sec" style={{ padding: '60px 40px 8px' }}>
        <div className="home-sec-inner">
          <div className="home-sec-head">
            <div>
              <div className="eyebrow-lg">Live webinars</div>
              <span className="home-live-pill">
                <span className="home-dot" />
                Free to attend, recordings sent after
              </span>
            </div>
            <Link to="/resources/webinars" className="home-see-all">See all →</Link>
          </div>
          <div className="home-webinars-list">
            {WEBINARS.map((w) => (
              <div key={w.title} className="home-webinar-row">
                <div className="home-webinar-date">
                  <div className="home-webinar-month">{w.month}</div>
                  <div className="home-webinar-day">{w.day}</div>
                </div>
                <div className="home-webinar-info">
                  <div className="home-webinar-title">{w.title}</div>
                  <div className="home-webinar-meta">{w.meta}</div>
                </div>
                <Link className="home-webinar-cta" to="/resources/webinars">
                  Save a seat
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSignup variant="band" source="landing" />

      {/* CTA */}
      <section className="home-cta">
        <div className="home-cta-band">
          <div className="home-cta-sweep" />
          <h2>Start your extraordinary journey here.</h2>
          <p>
            Pick a mentor, bring your questions, leave with a plan your family can act on this
            term.
          </p>
          <Link className="home-cta-btn" to="/mentors">
            Browse the room
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
